import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Product, PaginatedProducts } from '../types/Product';
import { Category, ProductCategoryFilter } from '../types/Category';
import { HttpCategoryProduct, HttpProduct, HttpCategory } from '../http';

interface Breadcrumb {
  label: string;
  href?: string;
}

interface Pagination {
  actualPage: number;
  cantPages: number;
  cantTotal: number;
}

interface ProductCategoryContextType {
  category: Category | null;
  breadcrumbs: Breadcrumb[];
  products: Product[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  pagination: Pagination;
  filter: ProductCategoryFilter;
  loadCategoryData: (sku: string) => Promise<void>;
  loadAllProductsData: (filter?: ProductCategoryFilter) => Promise<void>;
  loadSearchResults: (searchTerm: string, filter?: ProductCategoryFilter) => Promise<void>;
  loadMoreProducts: () => Promise<void>;
  saveListingCache: (key: string) => void;
  restoreListingCache: (key: string) => boolean;
  getCacheKey: (mode: string, param?: string) => string;
}

const ProductCategoryContext = createContext<ProductCategoryContextType | undefined>(undefined);

export const useProductCategoryContext = () => {
  const context = useContext(ProductCategoryContext);
  if (!context) {
    throw new Error('useProductCategoryContext must be used within a ProductCategoryProvider');
  }
  return context;
};

interface ProductCategoryProviderProps {
  children: ReactNode;
}

export const ProductCategoryProvider: React.FC<ProductCategoryProviderProps> = ({ children }) => {
  const [category, setCategory] = useState<Category | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<Pagination>({
    actualPage: 1,
    cantPages: 1,
    cantTotal: 0,
  });
  const [filter, setFilter] = useState<ProductCategoryFilter>({
    pagina: 1,
    tamanoPagina: 20,
  });

  const [cache, setCache] = useState<Record<string, any>>({});

  const loadCategoryData = useCallback(async (sku: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const response: PaginatedProducts = await HttpCategoryProduct.getProductsByCategorySku(sku, {
        pagina: 1,
        tamanoPagina: 20,
      });

      const lista = response.paginaProductosDTO?.lista || [];
      const paginacion = response.paginaProductosDTO?.paginacion;

      let categoriaData: Category | null = (response as any).categoria
        || (response as any).paginaProductosDTO?.categoria
        || (response as any).data?.categoria
        || null;

      if (!categoriaData) {
        try {
          const allCategories: Category[] = await HttpCategory.getAllCategories();
          categoriaData = allCategories.find(c => c.sku === sku) || null;
        } catch {
        }
      }

      setProducts(lista);
      setPagination({
        actualPage: paginacion?.actualPage || 1,
        cantPages: paginacion?.cantPages || 1,
        cantTotal: paginacion?.cantRecords || 0,
      });

      if (categoriaData) {
        setCategory(categoriaData);
        setBreadcrumbs([
          { label: 'Inicio', href: '/' },
          { label: 'Productos', href: '/productos' },
          { label: categoriaData.nombre },
        ]);
      } else {

        setCategory(null);
        setBreadcrumbs([
          { label: 'Inicio', href: '/' },
          { label: 'Productos', href: '/productos' },
          { label: sku },
        ]);
      }

      setFilter({ pagina: 1, tamanoPagina: 20 });
    } catch (err) {
      console.error('Error loading category:', err);
      setError('Error al cargar la categoría');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAllProductsData = useCallback(async (filterParam?: ProductCategoryFilter) => {
    try {
      setLoading(true);
      setError(null);

      const currentFilter = filterParam || filter;
      const response: PaginatedProducts = await HttpProduct.getAllProducts(currentFilter);

      const lista = response.paginaProductosDTO?.lista || [];
      const paginacion = response.paginaProductosDTO?.paginacion;

      setProducts(lista);
      setPagination({
        actualPage: paginacion?.actualPage || 1,
        cantPages: paginacion?.cantPages || 1,
        cantTotal: paginacion?.cantRecords || 0,
      });

      setBreadcrumbs([
        { label: 'Inicio', href: '/' },
        { label: 'Productos' },
      ]);

      setCategory(null);
      setFilter(currentFilter);
    } catch (err) {
      console.error('Error loading products:', err);
      setError('Error al cargar los productos');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const loadSearchResults = useCallback(async (searchTerm: string, filterParam?: ProductCategoryFilter) => {
    try {
      setLoading(true);
      setError(null);

      const currentFilter = filterParam || filter;
      const response: PaginatedProducts = await HttpProduct.getAllProducts(currentFilter, searchTerm);

      const lista = response.paginaProductosDTO?.lista || [];
      const paginacion = response.paginaProductosDTO?.paginacion;

      setProducts(lista);
      setPagination({
        actualPage: paginacion?.actualPage || 1,
        cantPages: paginacion?.cantPages || 1,
        cantTotal: paginacion?.cantRecords || 0,
      });

      setBreadcrumbs([
        { label: 'Inicio', href: '/' },
        { label: 'Búsqueda' },
      ]);

      setCategory(null);
      setFilter(currentFilter);
    } catch (err) {
      console.error('Error loading search results:', err);
      setError('Error al cargar los resultados');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const loadMoreProducts = useCallback(async () => {
    if (loadingMore || pagination.actualPage >= pagination.cantPages) {
      return;
    }

    try {
      setLoadingMore(true);
      const nextPage = pagination.actualPage + 1;
      const nextFilter = { ...filter, pagina: nextPage };

      let response: PaginatedProducts;

      if (category) {
        response = await HttpCategoryProduct.getProductsByCategorySku(category.sku, nextFilter);
      } else {
        response = await HttpProduct.getAllProducts(nextFilter);
      }

      const lista = response.paginaProductosDTO?.lista || [];
      const paginacion = response.paginaProductosDTO?.paginacion;

      setProducts(prev => [...prev, ...lista]);
      setPagination({
        actualPage: paginacion?.actualPage || nextPage,
        cantPages: paginacion?.cantPages || pagination.cantPages,
        cantTotal: paginacion?.cantRecords || pagination.cantTotal,
      });
      setFilter(nextFilter);
    } catch (err) {
      console.error('Error loading more products:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, pagination, filter, category]);

  const saveListingCache = useCallback((key: string) => {
    setCache(prev => ({
      ...prev,
      [key]: {
        category,
        breadcrumbs,
        products,
        pagination,
        filter,
      },
    }));
  }, [category, breadcrumbs, products, pagination, filter]);

  const restoreListingCache = useCallback((key: string): boolean => {
    const cached = cache[key];
    if (cached) {
      setCategory(cached.category);
      setBreadcrumbs(cached.breadcrumbs);
      setProducts(cached.products);
      setPagination(cached.pagination);
      setFilter(cached.filter);
      return true;
    }
    return false;
  }, [cache]);

  const getCacheKey = useCallback((mode: string, param?: string): string => {
    if (mode === 'category' && param) {
      return `category:${param}`;
    } else if (mode === 'search' && param) {
      return `search:${param}`;
    }
    return 'products';
  }, []);

  const value: ProductCategoryContextType = {
    category,
    breadcrumbs,
    products,
    loading,
    loadingMore,
    error,
    pagination,
    filter,
    loadCategoryData,
    loadAllProductsData,
    loadSearchResults,
    loadMoreProducts,
    saveListingCache,
    restoreListingCache,
    getCacheKey,
  };

  return (
    <ProductCategoryContext.Provider value={value}>
      {children}
    </ProductCategoryContext.Provider>
  );
};

